import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Empty,
  Input,
  Space,
  Spin,
  Tag,
  Timeline,
  Typography,
} from 'antd';
import { HistoryOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { get } from '../../api/client';

/** 留痕行。键名按线上拼写（camelCase）声明：全局 ResponseInterceptor 已把 snake 转成 camel */
interface HistoryRow {
  id: string;
  orderId: string;
  orderNumber: string;
  /** 中文动作文本，由服务端在业务事务里写入（见 orders/order-audit.ts） */
  operation: string;
  operatorId: string;
  /** 操作人取不到 Party 姓名时后端给空串，本端显示「未知操作人」 */
  operatorName: string;
  operationTime: string;
  /** JsonB 载荷：变更集或核算线索，形如 { changes: {...} } / { batchNo, nettingAmount } */
  details: Record<string, unknown> | null;
}

/** 定位订单只需要这一小截档案 */
interface OrderBrief {
  id: string;
  orderNumber: string;
  orderCategory: string;
  activityName: string | null;
  serviceType: string | null;
  orderStartDate: string;
  orderEndDate: string | null;
  orderStatus: string;
  approvalStatus: string;
  paymentStatus: string;
  orderAmount: number;
  isDeleted: boolean;
  customerName: string | null;
  userName: string | null;
}

/**
 * `details` 里出现过的键 → 中文标签。
 *
 * 未知键不在这张表里也照样渲染（原键名 + 原值），因为写入方在后端业务事务里，
 * 新增一类留痕不该让管理端把新字段演成「没有这项」。
 */
const DETAIL_LABELS: Record<string, string> = {
  orderNumber: '订单号',
  orderStatus: '订单状态',
  approvalStatus: '审核状态',
  fromCustomerId: '原客户',
  toCustomerId: '新客户',
  slotDuplicateConfirmed: '档期重复已人工确认',
  batchNo: '抵消批号',
  nettingAmount: '抵消总额',
  offsetAmount: '本单抵消金额',
  mode: '抵消方式',
  customerName: '客户',
  needsApproval: '需要审批',
  reason: '原因',
};

/** `details.changes` 的字段 → 中文标签（订单编辑与付款信息更新共用这一份差集形状） */
const CHANGE_LABELS: Record<string, string> = {
  orderAmount: '订单金额',
  paidAmount: '已付金额',
  orderStartDate: '预约开始日期',
  orderEndDate: '预约结束日期',
  // 档期其余三格：预约日期不可改之后，改档期留下的就是这几行
  isFullDay: '全天服务',
  orderTimeStart: '服务开始时间',
  orderTimeEnd: '服务结束时间',
  arrivalDateTime: '到达时间',
  serviceType: '服务类型',
  paymentStatus: '付款状态',
  paymentMethod: '支付方式',
  paymentDate: '付款日期',
  paymentVoucher: '付款凭证',
};

const OPERATION_COLORS: Record<string, string> = {
  订单编辑: 'blue',
  改派客户: 'orange',
  付款信息更新: 'green',
  订单删除: 'red',
  订单抵消: 'purple',
  金额抵消: 'purple',
  抵消审核通过: 'green',
  抵消审核拒绝: 'red',
};

const MODE_LABELS: Record<string, string> = { order: '按订单抵消', amount: '按金额抵消' };

function isChangePair(v: unknown): v is { from: unknown; to: unknown } {
  return !!v && typeof v === 'object' && 'from' in (v as Record<string, unknown>);
}

/** 金额类键显示 ¥，其余原样；布尔按「有/无」（凭证那一项只有有无两种意思） */
function formatValue(key: string, v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return key === 'paymentVoucher' ? (v ? '有' : '无') : v ? '是' : '否';
  if (typeof v === 'number') return /Amount$/.test(key) ? `¥${v.toFixed(2)}` : String(v);
  if (key === 'mode') return MODE_LABELS[String(v)] ?? String(v);
  // 未知形状（后端新增了嵌套载荷）也要落出字来，不能剩一个 [object Object]
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

interface DetailLine {
  label: string;
  value: string;
  /** UUID 之类长值：截断显示，完整值挂在 title 上供复制 */
  full?: string;
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * 把一条留痕的 JsonB 载荷摊成可读写的一行行。
 * `changes` 是「字段：旧 → 新」的差集，单独成组放在最前面——对账争议问的就是这个。
 */
function detailLines(details: Record<string, unknown> | null): DetailLine[] {
  if (!details) return [];
  const lines: DetailLine[] = [];
  const changes = details.changes;
  if (changes && typeof changes === 'object') {
    for (const [key, pair] of Object.entries(changes as Record<string, unknown>)) {
      if (!isChangePair(pair)) continue;
      lines.push({
        label: CHANGE_LABELS[key] ?? key,
        value: `${formatValue(key, pair.from)} → ${formatValue(key, pair.to)}`,
      });
    }
  }
  for (const [key, v] of Object.entries(details)) {
    if (key === 'changes') continue;
    const raw = typeof v === 'string' ? v : JSON.stringify(v);
    lines.push({
      label: DETAIL_LABELS[key] ?? key,
      value: UUID_PATTERN.test(raw) ? `${raw.slice(0, 8)}…` : formatValue(key, v),
      full: raw,
    });
  }
  return lines;
}

export default function OrderHistoryPage() {
  const [input, setInput] = useState('');
  /** 已提交查询的订单号：与输入框分开，避免边打字边发请求 */
  const [orderNumber, setOrderNumber] = useState('');

  /**
   * 两步一次做完：先用订单号解析出订单 id，再按 id 取留痕。
   *
   * 为什么要绕这一步：后台没有订单列表，而 `GET /admin/order-history` 收的是 uuid
   * （`@IsUUID`，订单号会被挡成 400），订单号→id 现成的只有 `/admin/order-notes` 这一条。
   * 合并成一个查询，第二步失败也就整条查询失败——不会出现「档案是旧的、留痕是新的」这种半屏状态。
   */
  const { data, isFetching, isError, error, refetch } = useQuery({
    queryKey: ['admin-order-history', orderNumber],
    queryFn: async () => {
      const target = await get<{ order: OrderBrief }>('/admin/order-notes', { orderNumber });
      const rows = await get<HistoryRow[]>('/admin/order-history', { orderId: target.order.id });
      return { order: target.order, rows };
    },
    enabled: orderNumber.length > 0,
    retry: false,
  });

  const search = () => {
    const trimmed = input.trim();
    if (!trimmed) return;
    setOrderNumber(trimmed);
  };

  const order = data?.order;
  const rows = data?.rows ?? [];

  return (
    <Card
      title="订单操作留痕"
      extra={
        <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching} disabled={!orderNumber}>
          刷新
        </Button>
      }
    >
      <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
        留痕由服务端在业务事务里写入（编辑、改派、付款更新、抵消与抵消审核、删除），
        客户端无法自行登记。状态迁移不在这张表里——它在订单详情的「状态变更记录」。
        这里按订单号定位，后台没有订单列表。
      </Typography.Paragraph>

      <Space.Compact style={{ width: '100%', maxWidth: 520 }}>
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onPressEnter={search}
          placeholder="输入订单号，如 ORD-20260911-509582"
          allowClear
          prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,0.25)' }} />}
        />
        <Button type="primary" onClick={search} loading={isFetching}>
          查询
        </Button>
      </Space.Compact>

      {orderNumber && isError ? (
        <Alert
          style={{ marginTop: 16 }}
          type="warning"
          showIcon
          title={error instanceof Error ? error.message : '查询失败'}
          description="请确认订单号是否完整（形如 ORD-20260911-509582）。"
          action={
            <Button size="small" onClick={() => refetch()}>
              重试
            </Button>
          }
        />
      ) : null}

      {order ? (
        <>
          <Descriptions
            style={{ marginTop: 16 }}
            size="small"
            column={{ xs: 1, sm: 2, md: 3 }}
            bordered
            items={[
              { key: 'no', label: '订单号', children: order.orderNumber },
              { key: 'act', label: '活动名称', children: order.activityName || order.serviceType || '-' },
              { key: 'cat', label: '分类', children: order.orderCategory },
              {
                key: 'date',
                label: '预约日期',
                children: order.orderEndDate && order.orderEndDate !== order.orderStartDate
                  ? `${order.orderStartDate} ~ ${order.orderEndDate}`
                  : order.orderStartDate,
              },
              { key: 'status', label: '订单状态', children: <Tag>{order.orderStatus}</Tag> },
              { key: 'amount', label: '订单金额', children: `¥${order.orderAmount.toFixed(2)}` },
              { key: 'owner', label: '下单方', children: order.userName || '-' },
              { key: 'customer', label: '客户', children: order.customerName || '-' },
              ...(order.isDeleted
                ? [{ key: 'deleted', label: '档案', children: <Tag color="red">已删除订单</Tag> }]
                : []),
            ]}
          />

          <Typography.Title level={5} style={{ margin: '20px 0 12px' }}>
            <HistoryOutlined style={{ marginRight: 8 }} />
            操作历史
            {rows.length > 0 ? (
              <Typography.Text type="secondary" style={{ marginLeft: 8, fontWeight: 400 }}>
                共 {rows.length} 条
              </Typography.Text>
            ) : null}
          </Typography.Title>

          {/* 读成功且真的零条才走这个空态：查询失败在上面是 Alert，不会被演成「这张单干干净净」 */}
          {rows.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="该订单暂无操作留痕" />
          ) : (
            <Timeline
              items={rows.map((row) => {
                const lines = detailLines(row.details);
                return {
                  key: row.id,
                  color: OPERATION_COLORS[row.operation] ?? 'gray',
                  content: (
                    <div>
                      <Space size={6} wrap style={{ marginBottom: lines.length > 0 ? 6 : 0 }}>
                        <Tag color={OPERATION_COLORS[row.operation] ?? 'default'}>{row.operation}</Tag>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {row.operatorName || '未知操作人'} · {dayjs(row.operationTime).format('YYYY-MM-DD HH:mm')}
                        </Typography.Text>
                      </Space>
                      {lines.length > 0 ? (
                        <div>
                          {lines.map((line, i) => (
                            <div
                              key={`${line.label}-${i}`}
                              style={{ fontSize: 12, color: 'rgba(0,0,0,0.65)', lineHeight: '20px' }}
                            >
                              <Typography.Text type="secondary">{line.label}：</Typography.Text>
                              <span title={line.full}>{line.value}</span>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ),
                };
              })}
            />
          )}
        </>
      ) : (
        <Spin spinning={isFetching}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={orderNumber ? '未找到订单' : '输入订单号查看该单的操作留痕'}
            style={{ marginTop: 24 }}
          />
        </Spin>
      )}
    </Card>
  );
}
